import React from "react"
import {
  type EntityCategory,
  type MonetaryValue,
  type TransactionSummary,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import numberHelper, { CurrencyStyle } from "@digits-shared/helpers/numberHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { useOpeningEndingMoneyFlow } from "src/frontend/components/Shared/Transactions/Categories/hooks/useOpeningEndingMoneyFlow"
import { SVGIconComponent } from "src/shared/components/Icons/SVGIcon"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const Container = styled.div`
  width: 217px;
`

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.translucentWhite30};

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const CategoryName = styled.div`
  font-weight: ${fonts.weight.black};
`

const SubText = styled.div`
  color: ${colors.translucentWhite60};
  font-weight: ${fonts.weight.normal};
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-align: left;
`

const Content = styled.div`
  padding-top: 8px;

  display: flex;
  flex-direction: column;
  gap: 8px;

  font-weight: ${fonts.weight.normal};
`

const Icon = styled(SVGIconComponent)`
  width: 24px;
  height: 24px;
  ${svgPathStyles(colors.white, 1.5)};
`

interface Props {
  category: EntityCategory
  summary: TransactionSummary
}

export const BalanceToolTip: React.FC<Props> = ({ category, summary }) => {
  const { startDate, endDate } = useDates()
  const { openingMoneyFlow, endingMoneyFlow } = useOpeningEndingMoneyFlow({
    categoryType: category.type,
    summary,
  })
  const opening = moneyFlowHelper.toMonetaryValue(openingMoneyFlow)
  const ending = moneyFlowHelper.toMonetaryValue(endingMoneyFlow)
  const openingCurrency = numberHelper.currency(opening, {
    style: CurrencyStyle.Detail,
    absolute: false,
  })
  const endingCurrency = numberHelper.currency(ending, {
    style: CurrencyStyle.Detail,
    absolute: false,
  })

  const deltaValue = useDeltaPreviousValue(opening, ending)
  const deltaCurrency = numberHelper.currency(deltaValue, {
    style: CurrencyStyle.Detail,
    absolute: false,
  })

  return (
    <Container>
      <Header>
        <Icon subjectDisplayKey={category.displayKey || category.name || ""} />
        <div>
          <CategoryName>{category.name}</CategoryName>
          <SubText>{category.chartOfAccountsDisplayNumber}</SubText>
        </div>
      </Header>
      <Content>
        <Row>
          <div>
            <div>Opening Balance</div>
            <SubText>{startDate}</SubText>
          </div>
          <div>{openingCurrency}</div>
        </Row>
        <Row>
          <div>Activity</div>
          <div>{deltaCurrency}</div>
        </Row>
        <Row>
          <div>
            <div>Ending Balance</div>
            <SubText>{endDate}</SubText>
          </div>
          <div>{endingCurrency}</div>
        </Row>
      </Content>
    </Container>
  )
}

const useDeltaPreviousValue = (opening: MonetaryValue, closing: MonetaryValue) =>
  React.useMemo(() => monetaryValueHelper.subtractValues(closing, opening), [opening, closing])

function useDates() {
  const intervalOrigin = useIntervalOrigin()

  return React.useMemo(() => {
    const unit = dateTimeHelper.unitOfTimeForInterval(intervalOrigin.interval)
    const today = dayjs()

    let start = dateTimeHelper.startOfIntervalOrigin(intervalOrigin)
    let end = dateTimeHelper.endOfIntervalOrigin(intervalOrigin)

    if (intervalOrigin.intervalCount && intervalOrigin.intervalCount > 1) {
      start = dateTimeHelper
        .startOfIntervalOrigin({
          ...intervalOrigin,
          intervalCount: intervalOrigin.intervalCount - 1,
        })
        .startOf(unit)
    }

    if (end.endOf(unit).isAfter(today)) {
      end = today
    }

    return {
      startDate: start.format("MMMM DD, YYYY"),
      endDate: end.format("MMMM DD, YYYY"),
    }
  }, [intervalOrigin])
}
